import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const ParentHolidays = ({ onNavigate, userInfo }) => {
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parentData, setParentData] = useState({
    name: "Loading...",
    parentId: "...",
    relation: "Parent",
    profileImage: null
  });

  const [holidaysData, setHolidaysData] = useState({
    holidays: [],
    holidaysByMonth: {},
    upcoming: [],
    totalHolidays: 0,
    upcomingCount: 0
  });

  const [eventsData, setEventsData] = useState({
    events: [],
    upcoming: [],
    past: [],
    totalEvents: 0,
    upcomingCount: 0
  });

  // Month names for calendar
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Fetch data from API
  useEffect(() => {
    fetchHolidaysAndEvents();
  }, [userInfo]);

  const fetchHolidaysAndEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Starting API call to fetch holidays and events...');

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      console.log('🔑 Token found:', token ? 'Yes' : 'No');

      if (!token) {
        console.log('❌ No token found, using fallback data');
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      // Fetch parent profile data first for sidebar
      const profileUrl = `${API_LINK}/api/parent/profile`;
      console.log('🌐 Profile API URL:', profileUrl);

      const profileResponse = await fetch(profileUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Profile Response status:', profileResponse.status);

      if (profileResponse.ok) {
        const profileResult = await profileResponse.json();
        console.log('📦 Profile API Response:', profileResult);

        if (profileResult.success && profileResult.data) {
          const data = profileResult.data;
          console.log('✅ Processing profile API data...');

          // Update parent data
          setParentData({
            name: data.name || "Parent",
            parentId: data.parentId || "PARENT-001",
            relation: data.relation || "Parent",
            profileImage: data.profileImage || null
          });
        }
      }

      // Fetch holidays and events in parallel
      const [holidaysResponse, eventsResponse] = await Promise.all([
        fetch(`${API_LINK}/api/parent/holidays`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_LINK}/api/parent/events`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      console.log('📡 Holidays Response status:', holidaysResponse.status);
      console.log('📡 Events Response status:', eventsResponse.status);

      if (!holidaysResponse.ok || !eventsResponse.ok) {
        if (holidaysResponse.status === 401 || eventsResponse.status === 401) {
          console.log('🔐 Authentication failed');
          setError('Session expired. Please login again.');
          handleLogout();
          return;
        }
        throw new Error(`HTTP error! Holidays: ${holidaysResponse.status}, Events: ${eventsResponse.status}`);
      }

      const [holidaysResult, eventsResult] = await Promise.all([
        holidaysResponse.json(),
        eventsResponse.json()
      ]);

      console.log('📦 Holidays API Response:', holidaysResult);
      console.log('📦 Events API Response:', eventsResult);

      if (holidaysResult.success && holidaysResult.data) {
        setHolidaysData(holidaysResult.data);
        console.log('✅ Holidays data updated successfully');
      }

      if (eventsResult.success && eventsResult.data) {
        setEventsData(eventsResult.data);
        console.log('✅ Events data updated successfully');
      }

    } catch (error) {
      console.error('💥 Error fetching holidays and events:', error);

      if (error.message.includes('fetch')) {
        setError('Cannot connect to server. Please ensure the server is running');
      } else {
        setError(`Failed to load holidays and events: ${error.message}`);
      }

      if (userInfo) {
        console.log('🔄 Using fallback userInfo data');
        setParentData(prev => ({
          ...prev,
          name: userInfo.name || prev.name,
          parentId: userInfo.id || prev.parentId
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    console.log('🚪 Logging out...');
    sessionStorage.clear();
    localStorage.clear();

    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  // Handle navigation
  const handleNavigation = (page) => {
    switch (page) {
      case 'Dashboard':
        onNavigate('parent-dashboard');
        break;
      case 'Academic Performance':
        onNavigate('parent-academic-performance');
        break;
      case 'Fee Status':
        onNavigate('parent-fees-status');
        break;
      case 'Holidays & Events':
        // Stay on current page
        break;
      case 'Emergency Alert':
        onNavigate('parent-emergency-alert');
        break;
      case 'Logout':
        handleLogout();
        break;
      default:
        console.log(`Navigation to ${page} clicked`);
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const getHolidaysForDate = (date) => {
    return holidaysData.holidays.filter(holiday => {
      const holidayDate = new Date(holiday.date);
      return holidayDate.getDate() === date &&
        holidayDate.getMonth() === selectedMonth &&
        holidayDate.getFullYear() === selectedYear;
    });
  };

  const getEventsForDate = (date) => {
    return eventsData.events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getDate() === date &&
        eventDate.getMonth() === selectedMonth &&
        eventDate.getFullYear() === selectedYear;
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get holiday type color
  const getHolidayTypeColor = (type) => {
    switch (type) {
      case 'national':
        return { backgroundColor: '#fef3c7', color: '#92400e', border: '#fbbf24' };
      case 'religious':
        return { backgroundColor: '#f3e8ff', color: '#6b21a8', border: '#a78bfa' };
      case 'institutional':
        return { backgroundColor: '#dbeafe', color: '#1e40af', border: '#60a5fa' };
      default:
        return { backgroundColor: '#f0fdf4', color: '#166534', border: '#22c55e' };
    }
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} style={{ padding: '0.5rem' }}></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayHolidays = getHolidaysForDate(day);
      const dayEvents = getEventsForDate(day);
      const isToday = new Date().getDate() === day &&
        new Date().getMonth() === selectedMonth &&
        new Date().getFullYear() === selectedYear;

      days.push(
        <div
          key={day}
          style={{
            padding: '0.5rem',
            border: '1px solid #e5e7eb',
            minHeight: '80px',
            position: 'relative',
            backgroundColor: isToday ? '#f0fdf4' : '#ffffff',
            borderColor: isToday ? '#22c55e' : '#e5e7eb'
          }}
        >
          <div style={{
            fontWeight: isToday ? 'bold' : 'normal',
            color: isToday ? '#166534' : '#374151',
            marginBottom: '0.25rem',
            fontSize: '0.9rem'
          }}>
            {day}
          </div>

          {/* Holidays */}
          {dayHolidays.map((holiday, index) => (
            <div
              key={`holiday-${index}`}
              style={{
                fontSize: '0.7rem',
                padding: '0.125rem 0.25rem',
                borderRadius: '3px',
                marginBottom: '0.125rem',
                ...getHolidayTypeColor(holiday.type)
              }}
              title={`${holiday.name} - ${holiday.description}`}
            >
              🏖️ {holiday.name.length > 12 ? holiday.name.substring(0, 12) + '...' : holiday.name}
            </div>
          ))}

          {/* Events */}
          {dayEvents.map((event, index) => (
            <div
              key={`event-${index}`}
              style={{
                fontSize: '0.7rem',
                padding: '0.125rem 0.25rem',
                backgroundColor: '#dbeafe',
                color: '#1e40af',
                borderRadius: '3px',
                marginBottom: '0.125rem'
              }}
              title={`${event.title} - ${event.description}`}
            >
              🎉 {event.title.length > 12 ? event.title.substring(0, 12) + '...' : event.title}
            </div>
          ))}
        </div>
      );
    }

    return days;
  };

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Holidays & Events...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Holidays & Events
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            {error}
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1e7139';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#238548';
              }}
            >
              Try Again
            </button>

            <button
              onClick={handleLogout}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#555';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#666';
              }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="parent"
      onNavigate={handleNavigation}
      parentData={parentData}
      activePage="Holidays & Events"
      pageTitle="Holidays & Events"
    >
      {/* Header Section */}
      <section style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div>
            <h2 style={{
              color: '#333',
              display: 'flex',
              alignItems: 'center',
              gap: '0.8rem',
              margin: '0 0 0.5rem 0',
              fontSize: '2rem'
            }}>
              <span style={{ color: '#238548' }}>🏖️</span>
              Holidays & Events
            </h2>
            <p style={{ color: '#666', margin: 0 }}>
              Stay updated with university holidays, events, and important dates
            </p>
          </div>

          {/* View Toggle */}
          <div style={{ display: 'flex', background: '#f4f7fa', padding: '0.25rem', borderRadius: '8px' }}>
            <button
              onClick={() => setViewMode('calendar')}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                background: viewMode === 'calendar' ? '#ffffff' : 'transparent',
                color: viewMode === 'calendar' ? '#238548' : '#666',
                borderRadius: '6px',
                cursor: 'pointer',
                boxShadow: viewMode === 'calendar' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                fontWeight: 500
              }}
            >
              Calendar View
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                background: viewMode === 'list' ? '#ffffff' : 'transparent',
                color: viewMode === 'list' ? '#238548' : '#666',
                borderRadius: '6px',
                cursor: 'pointer',
                boxShadow: viewMode === 'list' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                fontWeight: 500
              }}
            >
              List View
            </button>
          </div>
        </div>

        {/* Month Selector for Calendar View */}
        {viewMode === 'calendar' && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#f8fafc',
            padding: '1rem',
            borderRadius: '8px'
          }}>
            <button
              onClick={() => {
                if (selectedMonth === 0) {
                  setSelectedMonth(11);
                  setSelectedYear(selectedYear - 1);
                } else {
                  setSelectedMonth(selectedMonth - 1);
                }
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.2rem',
                cursor: 'pointer',
                padding: '0.5rem',
                color: '#666'
              }}
            >
              ◀
            </button>
            <h3 style={{ margin: 0, color: '#333' }}>
              {monthNames[selectedMonth]} {selectedYear}
            </h3>
            <button
              onClick={() => {
                if (selectedMonth === 11) {
                  setSelectedMonth(0);
                  setSelectedYear(selectedYear + 1);
                } else {
                  setSelectedMonth(selectedMonth + 1);
                }
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.2rem',
                cursor: 'pointer',
                padding: '0.5rem',
                color: '#666'
              }}
            >
              ▶
            </button>
          </div>
        )}
      </section>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <section style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          marginBottom: '2rem'
        }}>
          {/* Weekday Headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            background: '#238548',
            color: '#ffffff',
            textAlign: 'center',
            fontWeight: 'bold',
            padding: '1rem 0'
          }}>
            {dayNames.map(day => (
              <div key={day}>{day}</div>
            ))}
          </div>

          {/* Calendar Days */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            borderLeft: '1px solid #e5e7eb',
            borderBottom: '1px solid #e5e7eb'
          }}>
            {generateCalendarDays()}
          </div>
        </section>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div style={{ display: 'grid', gap: '2rem' }}>
          {/* Upcoming Holidays */}
          <section style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              background: '#238548',
              color: '#ffffff',
              padding: '1rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <span style={{ fontSize: '1.5rem' }}>🏖️</span>
              <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Upcoming Holidays</h3>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {holidaysData.upcoming.length > 0 ? (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {holidaysData.upcoming.map((holiday, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      gap: '1.5rem',
                      padding: '1rem',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      borderLeft: '4px solid #238548'
                    }}>
                      <div style={{
                        textAlign: 'center',
                        minWidth: '80px',
                        padding: '0.5rem',
                        background: '#ffffff',
                        borderRadius: '6px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                          color: '#238548'
                        }}>
                          {new Date(holiday.date).getDate()}
                        </div>
                        <div style={{
                          fontSize: '0.8rem',
                          color: '#666',
                          textTransform: 'uppercase'
                        }}>
                          {new Date(holiday.date).toLocaleDateString('en-US', { month: 'short' })}
                        </div>
                      </div>
                      <div>
                        <h4 style={{ margin: '0 0 0.25rem 0', color: '#333' }}>{holiday.name}</h4>
                        <div style={{
                          display: 'inline-block',
                          fontSize: '0.8rem',
                          padding: '0.125rem 0.5rem',
                          borderRadius: '12px',
                          marginBottom: '0.5rem',
                          ...getHolidayTypeColor(holiday.type)
                        }}>
                          {holiday.type}
                        </div>
                        <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                          {holiday.description || 'No description available'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#666' }}>No upcoming holidays found.</p>
              )}
            </div>
          </section>

          {/* Upcoming Events */}
          <section style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <div style={{
              background: '#3b82f6',
              color: '#ffffff',
              padding: '1rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              <span style={{ fontSize: '1.5rem' }}>🎉</span>
              <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Upcoming Events</h3>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {eventsData.upcoming.length > 0 ? (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {eventsData.upcoming.map((event, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      gap: '1.5rem',
                      padding: '1rem',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      borderLeft: '4px solid #3b82f6'
                    }}>
                      <div style={{
                        textAlign: 'center',
                        minWidth: '80px',
                        padding: '0.5rem',
                        background: '#ffffff',
                        borderRadius: '6px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{
                          fontSize: '1.2rem',
                          fontWeight: 'bold',
                          color: '#3b82f6'
                        }}>
                          {new Date(event.date).getDate()}
                        </div>
                        <div style={{
                          fontSize: '0.8rem',
                          color: '#666',
                          textTransform: 'uppercase'
                        }}>
                          {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                        </div>
                      </div>
                      <div>
                        <h4 style={{ margin: '0 0 0.25rem 0', color: '#333' }}>{event.title}</h4>
                        <div style={{
                          display: 'inline-block',
                          fontSize: '0.8rem',
                          padding: '0.125rem 0.5rem',
                          borderRadius: '12px',
                          marginBottom: '0.5rem',
                          backgroundColor: '#dbeafe',
                          color: '#1e40af'
                        }}>
                          {event.time || 'All Day'}
                        </div>
                        <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                          {event.description || 'No description available'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#666' }}>No upcoming events found.</p>
              )}
            </div>
          </section>
        </div>
      )}
    </Layout>
  );
};

export default ParentHolidays;